import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { environment } from '../../../environments/environment';
import { AppAnalytics } from '../../core/logging/shared/analytics';
import { ILogger, LoggerService } from '../../core/shared/logger.service';
import { StartupService } from '../../core/shared/startup.service';
import { TranslationService } from '../../core/shared/translation.service';
import { UserNotificationsService } from '../../user-notifications/shared/user-notifications.service';
import { TokenService } from '../internal/token.service';
import { AuthProvider } from '../model/AuthProvider';
import { AuthUser } from '../model/AuthUser';
import { Role } from '../model/Role';

@Injectable()
export class AuthService {
  public user: AuthUser | undefined;

  private readonly storageKeys = {
    existingUrl: 'mcomplus/url',
    callbackRecoveryAttempted: 'mcomplus/auth/callback-recovery-attempted'
  };

  private readonly skipRoutes = ['/auth/callback', '/relogin/callback', '/connect/auth/error', '/connect/auth/logout'];

  private readonly organizationStorageKey = 'mcomplus/auth/toggledOrganization';
  private authRuntimeHandlersRegistered = false;
  private isLoggingOut = false;
  private logger: ILogger;

  constructor(
    protected injector: Injector,
    protected tokenService: TokenService,
    protected startupService: StartupService,
    private oidc: OidcSecurityService,
    private eventService: PublicEventsService,
    logger: LoggerService
  ) {
    this.logger = logger.getLogger('AuthService');
    this.registerAuthRuntimeHandlers();
  }

  protected get router(): Router {
    return this.injector.get(Router);
  }

  protected get translationService(): TranslationService {
    return this.injector.get(TranslationService);
  }

  private get userNotificationsService(): UserNotificationsService {
    return this.injector.get(UserNotificationsService);
  }

  // Lifecycle

  public init(): Observable<boolean> {
    this.logger.debug('Initializing');

    if (this.isSkipRoute(window.location.pathname)) {
      this.logger.debug('Skipping init on callback/error/logout route');
      this.startupService.finished('auth');
      return of(false);
    }

    return this.oidc.checkAuth().pipe(
      switchMap(({ isAuthenticated, idToken, errorMessage }) => {
        if (isAuthenticated && idToken) {
          this.logger.debug('Already authenticated. Loading user from id token.');
          this.loadUserFromIdToken(idToken);
          return of(true);
        } else if (isAuthenticated) {
          this.logger.error('Authenticated response missing id token.', errorMessage);
          this.startupService.finished('auth');
          this.router.navigate(['/connect/auth/error']);
          return of(false);
        } else {
          this.logger.debug('Not authenticated. Redirecting to login.');
          this.login(true);
          return of(false);
        }
      }),
      catchError((error) => {
        this.logger.error('Authentication initialization failed.', error);
        this.startupService.finished('auth');
        this.router.navigate(['/connect/auth/error']);
        return of(false);
      })
    );
  }

  public callback(): Observable<boolean> {
    this.logger.debug('Callback called. Handling auth info');

    return this.oidc.checkAuth().pipe(
      switchMap(({ isAuthenticated, idToken, errorMessage }) => {
        if (isAuthenticated && idToken) {
          return this.finishSuccessfulCallback(idToken);
        }

        this.logger.error('Callback: authentication failed.', {
          isAuthenticated,
          hasIdToken: !!idToken,
          errorMessage
        });

        return this.handleCallbackFailure(errorMessage);
      }),
      catchError((error) => {
        this.logger.error('Callback auth handling failed.', {
          error
        });
        return this.handleCallbackFailure(error);
      })
    );
  }

  public logout(): void {
    if (this.isLoggingOut) {
      this.logger.debug('Sign out already in progress. Ignoring duplicate request.');
      return;
    }

    this.isLoggingOut = true;
    this.logger.debug('Signing out');
    this.tokenService.clear();
    this.translationService.clear();
    // TODO: use logoffAndRevokeTokens() instead of logoff() when BE supports it
    this.oidc.logoff().pipe(first()).subscribe({
      error: (error) => {
        this.isLoggingOut = false;
        this.logger.error('Sign out failed.', error);
      }
    });
  }

  // Token refresh (called by AuthInterceptor on 401)

  public refreshToken(): Observable<string> {
    this.logger.debug('Forcing session refresh');
    return this.oidc.forceRefreshSession().pipe(
      map(({ isAuthenticated, accessToken }) => {
        if (!isAuthenticated || !accessToken) {
          throw new Error('Session refresh failed');
        }
        return accessToken;
      })
    );
  }

  public getAccessToken(): Observable<string> {
    return this.oidc.getAccessToken();
  }

  /**
   * Starts OIDC login and optionally preserves a return URL.
   * @param storeCurrentUrl When true, stores the current app URL before redirecting to identity provider.
   * @param explicitReturnUrl Optional URL to store explicitly as the post-login target.
   * @param redirectUrl Optional callback override used for special flows (for example relogin callback).
   */
  public login(storeCurrentUrl = true, explicitReturnUrl?: string): void {
    this.logger.debug('Starting login flow');
    this.preLoginClearing(storeCurrentUrl, explicitReturnUrl);
    this.oidc.authorize();
  }

  public storeReturnUrl(url: string): void {
    const normalizedUrl = this.normalizeReturnUrl(url);
    if (!this.shouldSkipReturnUrl(normalizedUrl)) {
      this.logger.debug('Storing return url for after login', normalizedUrl);
      sessionStorage.setItem(this.storageKeys.existingUrl, normalizedUrl);
    }
  }

  private normalizeReturnUrl(url: string): string {
    if (!url) {
      return '';
    }

    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return '';
    }
  }

  private shouldSkipReturnUrl(normalizedUrl: string): boolean {
    if (!normalizedUrl || normalizedUrl === '/' || normalizedUrl === '/index.html') {
      return true;
    }

    try {
      const parsed = new URL(normalizedUrl, window.location.origin);
      return this.isSkipRoute(parsed.pathname);
    } catch {
      return true;
    }
  }

  private isSkipRoute(pathname: string): boolean {
    const normalizedPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
    return this.skipRoutes.includes(normalizedPath);
  }

  // Profile & organization
  public editProfile(): void {
    this.logger.debug('Redirecting to edit profile');
    const params = new URLSearchParams();
    params.set('redirect_uri', window.location.href);
    window.location.href = environment.authOptions['selfcareUrl'] + '?' + params;
  }

  public changeOrganization(organizationId: string): void {
    if (this.user && organizationId !== this.user?.organization) {
      this.user.organization = organizationId;

      const tokenServiceUser = this.tokenService.userProfile;
      tokenServiceUser.organization = organizationId;
      this.tokenService.userProfile = tokenServiceUser;

      sessionStorage.setItem(this.organizationStorageKey, organizationId);
      this.router.navigate(['/']);
    }
  }
  public getUserInitials(): string {
    let result = '';
    if (this.user) {
      if (this.user.givenName) { result += this.user.givenName[0]; }
      if (this.user.familyName) { result += this.user.familyName[0]; }
    }
    return result;
  }

  // Role helpers

  public hasRole(role: string): boolean {
    return !!this.user && this.user.roles && this.user.roles.includes(role);
  }

  public hasAtLeastOneOfGivenRoles(givenRoles: string[]): boolean {
    return !!this.user && this.user.roles && this.user.roles.some(role => givenRoles.includes(role));
  }

  public hasOnlyGivenRole(role: string): boolean {
    return !!this.user && this.user.roles && this.user.roles.length === 1 && this.user.roles[0] === role;
  }

  public isReader(): boolean { return this.hasRole(Role.customerReader); }

  public isCustomer(): boolean {
    return [Role.customerAdministrator, Role.customerEngineer, Role.customerReader,
    Role.customerImplementer, Role.customerManufacturer].some(r => this.hasRole(r));
  }

  public isProvider(): boolean {
    return this.hasRole(Role.administrator) || this.hasRole(Role.engineer);
  }

  public isEngineer(): boolean {
    return this.hasRole(Role.engineer) || this.hasRole(Role.customerEngineer);
  }

  public isPowerEngineer(): boolean { return this.hasRole(Role.powerEngineer); }

  public isProjectEngineer(): boolean { return this.hasRole(Role.customerManufacturer); }

  public isAdministrator(): boolean {
    return this.hasRole(Role.administrator) || this.hasRole(Role.customerAdministrator);
  }

  public isProviderAdministrator(): boolean { return this.hasRole(Role.administrator); }

  public isCustomerAdministrator(): boolean { return this.hasRole(Role.customerAdministrator); }

  public isOnlyCustomerAdministrator(): boolean { return this.hasOnlyGivenRole(Role.customerAdministrator); }

  public isCustomerEngineer(): boolean { return this.hasRole(Role.customerEngineer); }

  public isCustomerImplementer(): boolean { return this.hasRole(Role.customerImplementer); }

  public isCustomerManufacturer(): boolean { return this.hasRole(Role.customerManufacturer); }

  public isCustomerReader(): boolean { return this.hasRole(Role.customerReader); }

  public isEngineerOrCustomerEngineer(): boolean {
    return [Role.engineer, Role.powerEngineer, Role.customerEngineer].some(r => this.hasRole(r));
  }

  public isProviderEngineer(provider?: AuthProvider): boolean {
    return this.hasRole(Role.engineer) && (provider ? this.hasOrganization(provider.id) : true);
  }

  public isProviderPowerEngineer(provider?: AuthProvider): boolean {
    return this.hasRole(Role.powerEngineer) && (provider ? this.hasOrganization(provider.id) : true);
  }

  public isInternalEngineer(): boolean { return this.hasRole(Role.engineer); }

  public isRestricted(): boolean {
    return !!this.user && !!this.user.organization && (this.user.division != null || this.user.site != null);
  }

  // Private helpers

  private loadUserFromIdToken(idToken: string): void {
    this.populateUserFromTokenClaims(idToken);
    AppAnalytics.setUserId(this.user?.id);
    this.startupService.finished('auth');
  }

  private populateUserFromTokenClaims(idToken: string): void {
    const decoded = this.tokenService.decodeToken(idToken);

    this.logger.debug('Id token decoded', decoded);

    let organizations: string[] = decoded['mcomplus.organization'];
    if (!Array.isArray(organizations)) {
      organizations = organizations ? [organizations] : [];
    }

    const user: AuthUser = {
      id: decoded['sub'],
      name: (decoded['given_name'] || '') + ' ' + (decoded['family_name'] || ''),
      givenName: decoded['given_name'],
      familyName: decoded['family_name'],
      email: decoded['email'],
      roles: !decoded['role'] || Array.isArray(decoded['role']) ? decoded['role'] : [decoded['role']],
      organizations,
      organization: this.getToggledOrganization(organizations),
      provider: decoded['mcomplus.provider'],
      division: decoded['mcomplus.division'],
      site: decoded['mcomplus.site']
    };

    this.tokenService.userProfile = user;
    this.user = user;
  }

  private registerAuthRuntimeHandlers(): void {
    if (this.authRuntimeHandlersRegistered) {
      return;
    }

    this.authRuntimeHandlersRegistered = true;

    this.eventService.registerForEvents()
      .pipe(filter(e => e.type === EventTypes.CheckingAuthFinishedWithError))
      .subscribe((event) => {
        this.logger.error('OIDC checkAuth finished with error.', event.value);
      });

    this.eventService.registerForEvents()
      .pipe(filter(e => e.type === EventTypes.SilentRenewStarted))
      .subscribe(() => {
        this.logger.debug('Silent renew started.');
      });

    this.eventService.registerForEvents()
      .pipe(filter(e => e.type === EventTypes.SilentRenewFailed))
      .subscribe(() => {
        this.logger.warn('Silent renew failed. Triggering full re-login.');
        this.login(true);
      });
  }

  private hasOrganization(organization: string): boolean {
    return !!this.user && !!this.user.organization && this.user.organization === organization;
  }

  private getToggledOrganization(organizations: string[]): string {
    const toggled = sessionStorage.getItem(this.organizationStorageKey);
    if (!toggled) {
      const initialOrganization = organizations[0] || '';
      sessionStorage.setItem(this.organizationStorageKey, initialOrganization);
      return initialOrganization;
    }
    return toggled;
  }

  private preLoginClearing(storeCurrentUrl = true, explicitReturnUrl?: string): void {
    this.logger.debug('Clearing stored user info before login');
    this.tokenService.clear();
    if (storeCurrentUrl) {
      this.storeReturnUrl(this.getCurrentAppUrl());
    }
    if (explicitReturnUrl) {
      this.storeReturnUrl(explicitReturnUrl);
    }
  }

  private getCurrentAppUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  private getStoredReturnUrl(): string | null {
    return sessionStorage.getItem(this.storageKeys.existingUrl);
  }

  private clearStoredReturnUrl(): void {
    sessionStorage.removeItem(this.storageKeys.existingUrl);
  }

  private finishSuccessfulCallback(idToken: string): Observable<boolean> {
    sessionStorage.removeItem(this.storageKeys.callbackRecoveryAttempted);
    this.loadUserFromIdToken(idToken);
    const existingUrl = this.getStoredReturnUrl();
    if (existingUrl) {
      this.logger.debug('Redirecting to existing url', existingUrl);
      this.clearStoredReturnUrl();
      this.router.navigateByUrl(existingUrl);
    } else {
      this.logger.debug('Redirecting to root');
      this.router.navigate(['/']);
    }

    return this.userNotificationsService.init();
  }

  private handleCallbackFailure(error: unknown): Observable<boolean> {
    const hasRecoveryBeenAttempted = sessionStorage.getItem(this.storageKeys.callbackRecoveryAttempted) === 'true';

    // After backend restarts/redeploys, stale callback state can appear.
    // Recover once by clearing local auth state and restarting authorization.
    if (!hasRecoveryBeenAttempted) {
      sessionStorage.setItem(this.storageKeys.callbackRecoveryAttempted, 'true');
      this.logger.warn('Callback failed. Attempting one-time local auth recovery.', { error });
      this.oidc.logoffLocal();
      this.login(false);
      return of(false);
    }

    this.logger.error('Callback failed. Callback recovery already attempted. Navigating to auth error.', { error });
    this.router.navigate(['/connect/auth/error']);
    return of(false);
  }
}
